import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import { PageHeader } from '../../components/Layout';
import { otpsService, ActiveOTP } from '../../services';

/**
 * Format an ISO timestamp as a compact human string.
 * Never throws on bad input — returns the raw string as a fallback.
 */
const formatDateTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
};

/**
 * Nicely format an expires-in duration in seconds → "8m 42s" style.
 */
const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const OTPs: React.FC = () => {
  const [items, setItems] = useState<ActiveOTP[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Fetch first page (or after refresh) whenever refreshCounter bumps.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await otpsService.listActiveOTPs({ limit: 50 });
        if (cancelled) return;
        setItems(res.items);
        setCursor(res.pagination.hasMore ? res.pagination.lastEvaluatedKey : undefined);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load OTPs';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshCounter]);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    setLoading(true);
    setError(null);
    try {
      const res = await otpsService.listActiveOTPs({ limit: 50, lastEvaluatedKey: cursor });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.pagination.hasMore ? res.pagination.lastEvaluatedKey : undefined);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load more OTPs';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  const isMaskedForCaller = useMemo(
    () => items.length > 0 && items.every((i) => i.codeIsMasked),
    [items],
  );

  return (
    <Box
      className="page-container"
      sx={{
        padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PageHeader title="Active OTPs" />
      <Typography variant="body2" color="text.secondary">
        Non-expired, unused mobile-login codes.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => setRefreshCounter((c) => c + 1)}
          disabled={loading}
        >
          Refresh
        </Button>
        {isMaskedForCaller && (
          <Chip
            label="Codes are masked (super_admin required for plaintext)"
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ overflow: 'auto', flex: 1 }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Phone</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Expires in</TableCell>
                <TableCell>Expires at</TableCell>
                <TableCell>Created at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No active OTPs
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((otp) => (
                  <TableRow key={`${otp.phone}#${otp.expiresAt}`} hover>
                    <TableCell>{otp.phone}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{otp.code}</TableCell>
                    <TableCell>{formatDuration(otp.expiresInSeconds)}</TableCell>
                    <TableCell>{formatDateTime(otp.expiresAt)}</TableCell>
                    <TableCell>{formatDateTime(otp.createdAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {cursor && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OTPs;
